import {
  applyHeaderCheckboxChange,
  applyRowCheckboxChange,
  getHeaderCheckboxState,
  getVisibleSelectedKeys,
  isSelectAllRowSelected,
} from "@shared/lib/hooks/selectAllCheckboxSelection";
import { BriefCampaignInf } from "@shared/lib/types/briefCampains";
import { Checkbox } from "@shared/ui/Checkbox";
import type { TableProps } from "antd/es/table";
import classNames from "classnames";
import { useMemo } from "react";
import styles from "./styles.module.scss";

type UseExecutionRowSelectionArgs<
  T extends { _id: string; briefInfluencerId?: string } = BriefCampaignInf
> = {
  rows: { _id: string }[];
  totalCount: number;
  selectAll: boolean;
  checkedList: string[];
  uncheckList: string[];
  setSelectAll: (value: boolean) => void;
  setCheckedList: (list: string[]) => void;
  setUncheckList: (list: string[]) => void;
  hasActiveSearch: boolean;
  highlightedRecordId?: string;
};

export function useExecutionRowSelection<
  T extends { _id: string; briefInfluencerId?: string } = BriefCampaignInf
>({
  rows,
  totalCount,
  selectAll,
  checkedList,
  uncheckList,
  setSelectAll,
  setCheckedList,
  setUncheckList,
  hasActiveSearch,
  highlightedRecordId,
}: UseExecutionRowSelectionArgs<T>): TableProps<T>["rowSelection"] {
  const visibleIds = useMemo(() => rows.map((row) => row._id), [rows]);
  const total = totalCount > 0 ? totalCount : rows.length;
  const setters = {
    setSelectAll,
    setCheckedList,
    setUncheckList,
  };

  return {
    selectedRowKeys: getVisibleSelectedKeys(
      visibleIds,
      selectAll,
      checkedList,
      uncheckList
    ),
    checkStrictly: true,
    columnWidth: 24,
    fixed: true,
    getCheckboxProps: (record) => ({
      disabled: Boolean((record as any).isDeliverableRow),
      style: {
        display: (record as any).isDeliverableRow ? "none" : undefined,
      },
    }),
    renderCell: (_, record) => {
      if ((record as any).isDeliverableRow) {
        return null;
      }
      return (
        <div className={styles.selectionCell}>
          <Checkbox
            id={record._id}
          checked={isSelectAllRowSelected(
            record._id,
            selectAll,
            checkedList,
            uncheckList
          )}
          disabled={rows?.length === 0}
          onChange={(event) => {
            applyRowCheckboxChange({
              rowId: record._id,
              checked: event.target.checked,
              selectAll,
              checkedList,
              uncheckList,
              total,
              hasActiveSearch,
              setters,
            });
          }}
            className={classNames({
              [styles.highlightedCheckbox]:
                highlightedRecordId === record.briefInfluencerId ||
                highlightedRecordId === record._id,
            })}
          />
        </div>
      );
    },
    columnTitle: () => {
      const { checked, indeterminate } = getHeaderCheckboxState({
        selectAll,
        checkedList,
        uncheckList,
        visibleIds,
        total,
        hasActiveSearch,
      });

      return (
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          onChange={(event) => {
            applyHeaderCheckboxChange({
              checked: event.target.checked,
              selectAll,
              checkedList,
              uncheckList,
              visibleIds,
              hasActiveSearch,
              setters,
            });
          }}
        />
      );
    },
  };
}
